/** Variable-weight UPC/EAN scale barcode parser (mirrors backend barcode_helpers). */

export type WeightInputMethod = 'live' | 'barcode' | 'both';

export interface ScaleBarcodeProfile {
  format: 'variable_weight_upc_ean';
  prefix: string;
  pluLength: number;
  valueLength: number;
  weightDecimalPlaces: number;
  weightUnit: 'kg';
  checksum: 'upc_ean' | 'none';
  acceptLeadingEanZero: boolean;
}

export const DEFAULT_SCALE_BARCODE_PROFILE: ScaleBarcodeProfile = {
  format: 'variable_weight_upc_ean',
  prefix: '2',
  pluLength: 5,
  valueLength: 5,
  weightDecimalPlaces: 3,
  weightUnit: 'kg',
  checksum: 'upc_ean',
  acceptLeadingEanZero: true,
};

export type ScaleBarcodeParseFailReason =
  | 'empty'
  | 'non_numeric'
  | 'length'
  | 'prefix'
  | 'checksum'
  | 'weight_zero'
  | 'weight_invalid';

export interface ScaleBarcodeParseResult {
  ok: boolean;
  reason?: ScaleBarcodeParseFailReason;
  /** Recognized as this profile's structure (even if checksum failed). */
  recognized?: boolean;
  normalized?: string;
  scalePlu?: string;
  scalePluPadded?: string;
  rawWeight?: string;
  weight?: number;
  checkDigit?: string;
  weightUnit?: string;
}

export function normalizeBarcodeDigits(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value).replace(/\s+/g, '').trim();
}

/** UPC-A (12) / EAN-13 (13) check digit validation. */
export function validateUpcEanChecksum(code: string): boolean {
  const digits = normalizeBarcodeDigits(code);
  if (!/^\d{12}$|^\d{13}$/.test(digits)) return false;
  const body = digits.slice(0, -1);
  const check = Number(digits.slice(-1));
  let sum = 0;
  const len = body.length;
  for (let i = 0; i < len; i++) {
    const n = Number(body[i]);
    // From the right: odd positions ×3, even ×1
    const fromRight = len - i;
    sum += fromRight % 2 === 1 ? n * 3 : n;
  }
  return (10 - (sum % 10)) % 10 === check;
}

/**
 * Normalize scanner output for the default variable-weight preset.
 * Only strips a leading 0 from a 13-digit code when it is a valid EAN-13
 * whose 12-digit body is a valid UPC-A.
 */
export function normalizeScaleBarcode(
  barcode: string,
  profile: ScaleBarcodeProfile = DEFAULT_SCALE_BARCODE_PROFILE,
): string | null {
  const code = normalizeBarcodeDigits(barcode);
  if (!code) return null;
  if (!/^\d+$/.test(code)) return null;

  if (code.length === 12) {
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(code)) return null;
    return code;
  }

  if (code.length === 13 && profile.acceptLeadingEanZero && code.startsWith('0')) {
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(code)) return null;
    const upc = code.slice(1);
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(upc)) return null;
    return upc;
  }

  return null;
}

export function parseScaleBarcode(
  barcode: string,
  profile: ScaleBarcodeProfile = DEFAULT_SCALE_BARCODE_PROFILE,
): ScaleBarcodeParseResult {
  const raw = normalizeBarcodeDigits(barcode);
  if (!raw) return { ok: false, reason: 'empty' };
  if (!/^\d+$/.test(raw)) return { ok: false, reason: 'non_numeric' };

  const expectedLen =
    profile.prefix.length + profile.pluLength + profile.valueLength + 1;
  const eanLen = expectedLen + 1; // leading 0

  let candidate = raw;
  let recognizedLength = false;

  if (raw.length === expectedLen) {
    recognizedLength = true;
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(raw)) {
      return { ok: false, reason: 'checksum', recognized: true };
    }
    candidate = raw;
  } else if (
    raw.length === eanLen &&
    profile.acceptLeadingEanZero &&
    raw.startsWith('0')
  ) {
    recognizedLength = true;
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(raw)) {
      return { ok: false, reason: 'checksum', recognized: true };
    }
    const upc = raw.slice(1);
    if (profile.checksum === 'upc_ean' && !validateUpcEanChecksum(upc)) {
      return { ok: false, reason: 'checksum', recognized: true };
    }
    candidate = upc;
  } else {
    return { ok: false, reason: 'length' };
  }

  if (candidate.length !== expectedLen) {
    return { ok: false, reason: 'length', recognized: recognizedLength };
  }

  const prefix = candidate.slice(0, profile.prefix.length);
  if (prefix !== profile.prefix) {
    return { ok: false, reason: 'prefix' };
  }

  const pluStart = profile.prefix.length;
  const pluEnd = pluStart + profile.pluLength;
  const valEnd = pluEnd + profile.valueLength;
  const pluPadded = candidate.slice(pluStart, pluEnd);
  const rawWeight = candidate.slice(pluEnd, valEnd);
  const checkDigit = candidate.slice(valEnd);

  if (!/^\d+$/.test(pluPadded) || !/^\d+$/.test(rawWeight)) {
    return { ok: false, reason: 'non_numeric', recognized: true };
  }

  const divisor = 10 ** profile.weightDecimalPlaces;
  const weight = Number(rawWeight) / divisor;
  if (!Number.isFinite(weight) || weight < 0) {
    return { ok: false, reason: 'weight_invalid', recognized: true };
  }
  if (weight === 0) {
    return { ok: false, reason: 'weight_zero', recognized: true };
  }

  const scalePlu = pluPadded.replace(/^0+/, '') || pluPadded;

  return {
    ok: true,
    recognized: true,
    normalized: candidate,
    scalePlu,
    scalePluPadded: pluPadded,
    rawWeight,
    weight,
    checkDigit,
    weightUnit: profile.weightUnit,
  };
}
